/**
 * Shared «cast a spell from the sheet» logic — the full sheet's SpellsSection, the «Бой» tab and the battle HUD.
 *
 *   - a spell of level 1+ needs a slot of its level or higher (the lowest free one is spent); without one the cast is
 *     refused with a clear message (it went through with 0 slots — charsheet pass №37);
 *   - a concentration spell sets the concentration (another one ends) — №38;
 *   - an attack spell rolls to hit with its damage on the same card, named («Атака заклинанием: Fire Bolt») — №39;
 *   - a save / utility spell posts the spell card; its damage or healing is rolled right after (cantrips scale with the
 *     character level, «MOD» is the spellcasting modifier, Disciple of Life adds 2 + the slot level) — №39.
 */
import { toast } from 'sonner';
import type { HudSpell, HudCharacterData, AbilityStat } from '../../lib/character-hud-store';
import { helpers, statLabel } from '../../lib/character-hud-store';
import type { SpellCastInfo } from '@roll1/shared';
import { resolveClassSlug } from '@roll1/shared/systems/dnd5e/class-resolve.js';
import type { TranslationKeys } from '../../i18n/types';
import { isUnconsciousAtZero } from './death-state';

type TFn = (key: keyof TranslationKeys) => string;

export interface SpellCastCtx {
  data: HudCharacterData;
  send: (msg: unknown) => void;
  /** useRoll().rollSpellAttack — the to-hit card (with the damage) of an attack spell. */
  rollSpellAttack: (spell?: { name: string; damage?: { expression: string; type: string } }) => void;
  /** useRoll().rollCustom — the damage / healing roll of a save or healing spell. */
  rollCustom?: (name: string, expression: string) => void;
  /** Decrement a slot of the given level (store patch + server message). */
  spendSlot: (level: number) => void;
  t: TFn;
  sheetId?: string;
  /** The interface-language name of an SRD word (the damage type on the roll card). */
  label?: (name: string) => string;
}

/**
 * How a class readies its spells (SRD): bard, sorcerer, warlock, ranger cast every spell they KNOW (no preparing);
 * cleric, druid, wizard prepare ability mod + level (min 1), a paladin mod + half the level; a wizard prepares only
 * from the spellbook. An unknown (homebrew) class prepares without a cap.
 */
export function spellPrepRule(data: HudCharacterData): { mode: 'known' | 'prepared'; limit: number } {
  const slug = resolveClassSlug(data.classData?.[0]?.className || data.identity.className);
  const level = data.classData?.[0]?.level ?? data.identity.level ?? 1;
  const mod = helpers.mod(data.stats[data.spells.stat]);
  if (slug && ['bard', 'sorcerer', 'warlock', 'ranger'].includes(slug)) return { mode: 'known', limit: Infinity };
  if (slug === 'paladin') return { mode: 'prepared', limit: Math.max(1, mod + Math.floor(level / 2)) };
  if (slug && ['cleric', 'druid', 'wizard'].includes(slug)) return { mode: 'prepared', limit: Math.max(1, mod + level) };
  return { mode: 'prepared', limit: Infinity };
}

/** Castable now: a cantrip, an always-prepared or prepared spell — or any known spell of a «known» caster. */
export function isCastableNow(spell: HudSpell, rule: { mode: 'known' | 'prepared' }): boolean {
  return spell.level === 0 || spell.state === 'always' || spell.state === 'prepared' || (rule.mode === 'known' && spell.state === 'known');
}

/** The lowest slot level ≥ `level` with a slot left (pact slots count), or null. */
export function freeSlotLevel(data: HudCharacterData, level: number): number | null {
  const levels = Object.keys(data.spells.slots).map(Number).filter(l => l >= level).sort((a, b) => a - b);
  for (const l of levels) if ((data.spells.slots[l]?.current ?? 0) > 0) return l;
  return null;
}

/** Cantrip damage dice grow at character levels 5 / 11 / 17 («1d10» → «2d10»…). */
export function cantripDice(expr: string, characterLevel: number): string {
  const tier = characterLevel >= 17 ? 4 : characterLevel >= 11 ? 3 : characterLevel >= 5 ? 2 : 1;
  if (tier === 1) return expr;
  return expr.replace(/^(\d+)d(\d+)/, (_m, n: string, d: string) => `${Number(n) * tier}d${d}`);
}

/** «1d8+MOD» → «1d8+3» (+ Disciple of Life: 2 + slot level). */
export function healExpression(expr: string, data: HudCharacterData, slotLevel: number): string {
  const mod = helpers.mod(data.stats[data.spells.stat]);
  let out = expr.replace(/MOD/gi, String(mod)).replace(/\+-/g, '-');
  if (slotLevel > 0 && data.features.some(f => f.name === 'Disciple of Life')) out += `+${2 + slotLevel}`;
  return out;
}

/** Classes that may cast a ritual spell as a ritual (no slot, +10 minutes): cleric, druid, wizard (from the book), bard. */
export function canCastRitual(data: HudCharacterData): boolean {
  const slug = resolveClassSlug(data.classData?.[0]?.className || data.identity.className);
  return !!slug && ['cleric', 'druid', 'wizard', 'bard'].includes(slug);
}

/**
 * A spell cast with a higher slot: the extra dice its «At Higher Levels» text names («…increases by 1d8 for each slot
 * level above 1st») are added — «4d6» at level 2 → «4d6+1d6».
 */
export function upcastExpression(expr: string, higherLevels: string | undefined, spellLevel: number, slotLevel: number): string {
  if (!higherLevels || slotLevel <= spellLevel) return expr;
  const m = /increases by (\d+)d(\d+) for each slot level above/i.exec(higherLevels);
  if (!m) return expr;
  const n = (slotLevel - spellLevel) * Number(m[1]);
  return `${expr}+${n}d${m[2]}`;
}

/** @returns false when the cast is refused (no slot, dead, unconscious at 0 hit points). */
export function performSpellCast(spell: HudSpell, ctx: SpellCastCtx): boolean {
  const { data, send, rollSpellAttack, rollCustom, spendSlot, t, sheetId } = ctx;
  const label = ctx.label ?? ((n: string) => n);
  // GN (re-pass Н12): the spell in the interface language — toasts, the chat line, the roll names, the cast card
  const nm = label(spell.name) || spell.name;
  if (data.deathSaves?.status === 'dead') { toast(t('characterHud.deadNoRoll')); return false; }
  // At 0 hit points (dying / stable) — Unconscious, no spells, from whichever place the cast starts (GN review: the
  // check lived only in «Бой» and the HUD, «Персонаж → Заклинания» still cast).
  if (isUnconsciousAtZero(data)) { toast(t('characterHud.unconsciousNoAct')); return false; }

  let slotLevel = 0;
  let asRitual = false;
  if (spell.level > 0) {
    const free = freeSlotLevel(data, spell.level);
    if (free === null) {
      // A ritual spell of a ritual caster goes without a slot (10 minutes longer).
      if (spell.ritual && canCastRitual(data)) {
        asRitual = true;
        toast(t('characterHud.castAsRitual').replace('{spell}', nm));
      } else {
        toast.error(t('characterHud.noSlotForSpell').replace('{spell}', nm).replace('{n}', String(spell.level)));
        return false;
      }
    } else {
      slotLevel = free;
    }
  }

  if (spell.concentration && sheetId) {
    const current = data.spells.concentrationSpellId;
    const key = spell.spellSlug ?? spell.id;
    if (current && current !== key) {
      const prev = data.spells.known.find(s => (s.spellSlug ?? s.id) === current)?.name ?? current;
      toast(t('characterHud.concentrationReplaced').replace('{prev}', label(prev)).replace('{spell}', nm));
    }
    send({ type: 'sheet:concentration-set', sheetId, spellSlug: key });
  }

  const lvlLabel = spell.level === 0 ? 'заговор' : asRitual ? `${spell.level} круг, ритуал` : slotLevel > spell.level ? `${slotLevel} круг (из ${spell.level})` : `${spell.level} круг`;
  const damage = spell.roll?.damage
    ? (spell.level === 0 ? cantripDice(spell.roll.damage, data.identity.level) : upcastExpression(spell.roll.damage, spell.higherLevels, spell.level, slotLevel))
    : null;

  if (spell.roll?.attack === 'melee' || spell.roll?.attack === 'ranged') {
    rollSpellAttack({ name: nm, ...(damage ? { damage: { expression: damage, type: label(spell.roll.damageType ?? '') } } : {}) });
  } else {
    const dc = helpers.spellDC(data);
    const saveStat = spell.roll?.saveStat;
    const spellCast: SpellCastInfo = {
      name: nm,
      level: spell.level,
      school: spell.school,
      components: spell.components,
      concentration: spell.concentration,
      ritual: spell.ritual,
      castingTime: spell.castingTime,
      range: spell.range,
      duration: spell.duration,
      description: spell.description,
      higherLevels: spell.higherLevels,
      ...(saveStat ? { save: { ability: saveStat, dc, halfOnSuccess: spell.roll?.saveHalfOnSuccess } } : {}),
    };
    const text = saveStat
      ? `✦ ${nm} (${lvlLabel}) — спасбросок ${statLabel(t, saveStat as AbilityStat)} СЛ ${dc}`
      : `✦ Сотворяет: ${nm} (${lvlLabel})`;
    // The card is the character's (it was signed by the user — charsheet pass №40).
    send({ type: 'chat:message', text, spellCast, speakingAs: { name: data.identity.name } });
    if (rollCustom) {
      if (damage) rollCustom(`${nm} — урон${spell.roll?.damageType ? ` (${label(spell.roll.damageType)})` : ''}`, damage);
      else if (spell.roll?.heal) rollCustom(`${nm} — лечение`, upcastExpression(healExpression(spell.roll.heal, data, slotLevel || spell.level), spell.higherLevels, spell.level, slotLevel));
    }
  }

  if (slotLevel > 0) spendSlot(slotLevel);
  return true;
}
